<script lang="ts">
  import { createInfiniteQuery, createMutation } from '@tanstack/svelte-query';
  import type { Region } from '$lib/region';

  import DirectoryLayout from './directory-layout.svelte';
  import FilterBar from './filters/filter-bar.svelte';
  import CollectionGrid from './collection-grid.svelte';
  import FilterMenu from './filters/filter-menu.svelte';
  import LoadMore from './load-more.svelte';

  import { drawer } from '$lib/stores/drawer';
  import { shopify } from '$lib/shopify';
  import { aboveLg } from '$lib/stores/breakpoints';
  import { navbarInfo } from '$components/navigation/navbar.svelte';
  import { urlProductFilters } from '$lib/stores/url-product-filters';
  import { filterConfigurationSortKeys } from '$lib/stores/content';
  import { AnalyticsEvents, analyticsEvent } from '$lib/analytics';
  import { getTotalFromCollectionQueryFilters } from '$lib/utils/shopify/getTotalFromCollectionQueryFilters';

  import type { GetCollectionQuery, GetCollectionQueryVariables } from '$gql/storefront/operations';
  import type { LanguageCode, ProductCollectionSortKeys } from '$gql/storefront/schemas';
  import type { Filter } from '$gql/storefront/schemas';
  import { page } from '$app/stores';
  import { getValidNumberBetween } from '$lib/utils/number';
  import QuickSubcategoryFilter from './filters/quick-subcategory-filter.svelte';
  import { lang } from '$lib/lang';
  import { bannerInfo } from '$components/banner.svelte';
  import { optimalUSCountry, parseCountryAsCode } from '$lib/country';
  import type { MarketingItemStoryblok } from '$types/bloks';
  export let collectionName: string;
  export let collectionHandle: string;
  export let pageSize: number;
  export let subcategories: (number | string)[] | undefined = undefined;
  export let hidePromotionalBlocks: boolean | undefined = false;
  export let marketingItems: MarketingItemStoryblok[] | undefined = undefined;
  export let quizData:
    | { title?: string; email: string; answers: { [question: string]: string[] }[] }
    | undefined = undefined;

  /* GraphQL */ `
    query getCollection(
      $handle: String!
      $filters: [ProductFilter!]
      $lang: LanguageCode
      $sortKey: ProductCollectionSortKeys
      $reverse: Boolean
      $pageSize: Int!
      $after: String
      $country: CountryCode
    ) @inContext(language: $lang, country: $country) {
      collection(handle: $handle) {
        title
        products(
          first: $pageSize
          filters: $filters
          after: $after
          sortKey: $sortKey
          reverse: $reverse
        ) {
          nodes {
            ...ProductCard
          }
          pageInfo {
            hasNextPage
            endCursor
          }
          filters {
            ...Filter
          }
        }
      }
    }
  `;
  $: query = createInfiniteQuery<GetCollectionQuery, GetCollectionQueryVariables>({
    staleTime: 5 * 60 * 1000,
    queryKey: [
      'collection-directory',
      collectionHandle,
      sort.key,
      sort.reverse,
      $urlProductFilters,
      $lang
    ],
    queryFn: async ({ pageParam }) =>
      await $shopify.getCollection({
        handle: collectionHandle,
        pageSize,
        after: pageParam,
        sortKey: sort.key as ProductCollectionSortKeys,
        reverse: sort.reverse,
        filters: $urlProductFilters,
        lang: $lang.toUpperCase() as LanguageCode,
        country: parseCountryAsCode($optimalUSCountry)
      }),
    getNextPageParam: (last) => {
      if (last.collection?.products.pageInfo.hasNextPage) {
        return last.collection?.products.pageInfo.endCursor;
      } else {
        return;
      }
    }
  });

  const quizEventMutation = createMutation<
    { status: number; message: string },
    unknown,
    {
      email: string;
      region: Region;
      quizName?: string;
      resultsUrl?: string;
      answers: { [question: string]: string[] }[];
      products?: {
        id: string;
        title: string;
        handle: string;
        availableForSale: boolean;
        price: {
          minVariantPrice: {
            amount: string;
            currencyCode: string;
          };
        };
      }[];
    }
  >({
    mutationKey: ['klaviyoQuizEvent'],
    mutationFn: async (vars) => {
      const res = await fetch('/api/klaviyo/create-quiz-event', {
        method: 'POST',
        body: JSON.stringify(vars)
      });

      if (!res.ok) throw new Error('Error sending quiz event');

      return res.json();
    }
  });

  let sentQuizResults = false;
  $: if (!quizData) {
    sentQuizResults = false;
  }
  // Send quiz event when products are loaded and quiz data is available
  $: if ($query.data?.pages[0]?.collection?.products.nodes && quizData && !sentQuizResults) {
    $quizEventMutation.mutate({
      email: quizData.email,
      region: $page.params.region as Region,
      quizName: quizData.title,
      resultsUrl: $page.url.href,
      answers: quizData.answers,
      products: $query.data.pages[0].collection.products.nodes.map((product) => ({
        id: product.id,
        title: product.title,
        handle: product.handle,
        availableForSale: product.availableForSale,
        price: product.priceRange
      }))
    });
    sentQuizResults = true;
  }

  // Keep previous data
  $: if ($query.data?.pages) {
    previousData = $query.data?.pages;
    showing = ($query.data?.pages || []).reduce(
      (a, b) => a + (b.collection?.products.nodes.length || 0),
      0
    );
  }

  // Sort
  $: sortIdx = getValidNumberBetween(
    Number($page.url.searchParams.get('sort')),
    0,
    $filterConfigurationSortKeys.length
  );

  $: sortFilters = $filterConfigurationSortKeys.filter((sortItem) => !sortItem.hideFilter);

  $: sort = sortFilters[sortIdx];

  // Filters
  let filters: Filter[] | undefined = $query?.data?.pages[0]?.collection?.products?.filters;

  $: if (!filters && $query?.data?.pages[0]?.collection?.products.filters) {
    filters = $query?.data?.pages[0]?.collection.products.filters;
  }

  $: total = $query.data?.pages[0].collection?.products.filters
    ? getTotalFromCollectionQueryFilters($query.data?.pages[0].collection?.products.filters)
    : null;

  let showing = 0;
  let gridRef: HTMLElement;
  let counter = 0;
  let expanded = false;
  let previousData: GetCollectionQuery[] | undefined;

  $: if (expanded && !$aboveLg) drawer.open('filters', { cleanup: () => (expanded = false) });

  $: scrollToTop = () => {
    const top =
      window?.scrollY +
      gridRef?.getBoundingClientRect().top -
      ($navbarInfo?.rect.height || 65) -
      ($bannerInfo.isSticky ? $bannerInfo.rect?.height || 56 : 56);
    if (gridRef?.getBoundingClientRect().top - ($navbarInfo?.rect.height || 65) < 0) {
      window.requestAnimationFrame(() => window.scrollTo({ top: top - 48, behavior: 'smooth' }));
    }
  };
</script>

<DirectoryLayout {expanded} on:submit={scrollToTop}>
  <div slot="top">
    {#if subcategories && subcategories.length}
      <QuickSubcategoryFilter class="my-2 mb-4" items={subcategories} />
    {/if}
    <FilterBar
      sortkeys={sortFilters}
      on:toggleFilterMenu={() => (expanded = !expanded)}
      {total}
      menuOpen={expanded}
      loading={$query.isLoading}
      class="container mx-auto py-4"
    />
  </div>
  <CollectionGrid
    bind:ref={gridRef}
    {pageSize}
    {showing}
    pages={$query.data?.pages}
    {previousData}
    loading={$query.isLoading}
    threeCol={expanded}
    {hidePromotionalBlocks}
    {marketingItems}
  />
  <FilterMenu slot="sidebar" sortkeys={sortFilters} {filters} loading={!filters} />
  <LoadMore
    slot="load-more"
    {total}
    {showing}
    loading={$query.isFetchingNextPage}
    hasNextPage={$query.hasNextPage}
    on:loadMore={() => {
      $query.fetchNextPage();
      counter += 1;
      analyticsEvent({
        event: AnalyticsEvents.SHOP_LOAD_MORE,
        collection_name: collectionName,
        nth_session_click: counter
      });
    }}
  />
</DirectoryLayout>
